import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useSessionStore } from '@/stores/useSessionStore'
import { AuthBootstrapSkeleton } from '@/shared/components/skeletons'
import { useMe } from '@/features/auth/api'
import {
  isCompletingInitialPasswordSetup,
  isPasswordSetupGuestPath,
} from '@/features/auth/lib/initial-password-setup'

/**
 * Public auth routes: guests see the outlet; authenticated users go home.
 * Exception: initial password setup may use forgot-password → reset-password while session exists.
 */
export function RequireGuest() {
  const location = useLocation()
  const {
    isAuthenticated,
    user,
    explicitLogout,
    clearSession,
    passwordSetupRequired,
    passwordSetupDeadlineMs,
  } = useSessionStore()
  const { isLoading, isPending, isError } = useMe()

  const completingInitialPasswordSetup = isCompletingInitialPasswordSetup(
    passwordSetupRequired,
    passwordSetupDeadlineMs
  )
  const allowPasswordSetupGuestRoute =
    completingInitialPasswordSetup &&
    isPasswordSetupGuestPath(location.pathname)

  useEffect(() => {
    if (!isError || isAuthenticated || user) return
    clearSession()
  }, [isError, isAuthenticated, user, clearSession])

  if (isAuthenticated && user && !allowPasswordSetupGuestRoute) {
    return <Navigate to="/" replace />
  }

  if (isError) {
    return <Outlet />
  }

  if (
    !explicitLogout &&
    !isAuthenticated &&
    !user &&
    (isLoading || isPending)
  ) {
    return <AuthBootstrapSkeleton />
  }

  return <Outlet />
}
